import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";

export default function AdminCreateMembership() {
  // ---------------- STATES ----------------
  const [membershipType, setMembershipType] = useState("basic");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [intendedLearner, setIntendedLearner] = useState("");
  const [availableCourses, setAvailableCourses] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [curriculumCourses, setCurriculumCourses] = useState([]);
  const [accessType, setAccessType] = useState("free");
  const [price, setPrice] = useState("");
  const [expiryType, setExpiryType] = useState("lifetime");
  const [expiryMonths, setExpiryMonths] = useState("");
  const [expiryYears, setExpiryYears] = useState("");
  const [status, setStatus] = useState("draft");
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState(null);
  const [promoVideoUrl, setPromoVideoUrl] = useState("");
  const [membershipMessage, setMembershipMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState({
    basic: false,
    pro: false,
    enterprise: false,
    free: false,
    paid: false,
    lifetime: false,
    expiry: false,
    published: false,
    draft: false
  });

  const API_CREATE = "https://kaasbackend.vetrinahealthcare.com/lms/membership/create/";

  // ---------------- THEME ----------------
  const theme = {
    primary: "#6D28D9", // Purple
    secondary: "#7C3AED", // Light Purple
    accent: "#F97316", // Orange
    lightAccent: "#FB923C",
    background: "#F8FAFC",
    cardBg: "#FFFFFF",
    text: "#1F2937",
    lightText: "#6B7280",
    border: "#E5E7EB",
    error: "#EF4444",
    success: "#10B981"
  };

  // ---------------- RESPONSIVE STYLES ----------------
  const styles = {
    page: {
      minHeight: "100vh",
      background: theme.background,
      padding: "1rem",
      fontFamily: "'Inter', sans-serif",
      '@media (min-width: 768px)': {
        padding: "2rem",
      }
    },
    container: {
      maxWidth: "1400px",
      margin: "0 auto",
      background: theme.cardBg,
      padding: "1.5rem",
      borderRadius: "1rem",
      boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
      '@media (min-width: 768px)': {
        padding: "2rem",
      }
    },
    heading: {
      fontSize: "1.75rem",
      fontWeight: "800",
      color: theme.primary,
      marginBottom: "0.5rem",
      '@media (min-width: 768px)': {
        fontSize: "2.5rem",
      }
    },
    sub: {
      color: theme.lightText,
      marginBottom: "2rem",
      fontSize: "0.875rem",
      '@media (min-width: 768px)': {
        fontSize: "1rem",
      }
    },
    label: {
      fontSize: "0.875rem",
      fontWeight: "600",
      color: theme.primary,
      marginBottom: "0.5rem",
      display: "block",
    },
    input: {
      width: "100%",
      padding: "0.75rem 1rem",
      borderRadius: "0.75rem",
      border: `2px solid ${theme.border}`,
      marginBottom: "1rem",
      fontSize: "1rem",
      transition: "all 0.3s ease",
      background: theme.cardBg,
      '&:focus': {
        outline: 'none',
        borderColor: theme.primary,
        boxShadow: `0 0 0 3px ${theme.primary}20`,
      }
    },
    textarea: {
      width: "100%",
      padding: "0.75rem 1rem",
      borderRadius: "0.75rem",
      border: `2px solid ${theme.border}`,
      height: "120px",
      marginBottom: "1rem",
      fontSize: "1rem",
      resize: "vertical",
      transition: "all 0.3s ease",
      background: theme.cardBg,
      '&:focus': {
        outline: 'none',
        borderColor: theme.primary,
        boxShadow: `0 0 0 3px ${theme.primary}20`,
      }
    },
    btnChip: (active, loading) => ({
      padding: "0.75rem 1.5rem",
      borderRadius: "2rem",
      border: `2px solid ${active ? theme.accent : theme.lightAccent}`,
      background: active ? theme.accent : "transparent",
      color: active ? "white" : theme.accent,
      fontWeight: "600",
      cursor: loading ? "not-allowed" : "pointer",
      marginRight: "0.75rem",
      marginBottom: "0.5rem",
      transition: "all 0.3s ease",
      opacity: loading ? 0.7 : 1,
      position: "relative",
      minWidth: "100px",
      fontSize: "0.875rem",
      '@media (min-width: 768px)': {
        fontSize: "1rem",
        marginBottom: "0",
      }
    }),
    section: {
      marginBottom: "2rem",
      padding: "1.5rem",
      background: theme.background,
      borderRadius: "1rem",
      border: `1px solid ${theme.border}`,
    },
    card: {
      background: theme.cardBg,
      padding: "1.25rem",
      borderRadius: "0.75rem",
      marginTop: "0.75rem",
      border: `2px solid ${theme.border}`,
      transition: "all 0.3s ease",
    },
    submit: {
      background: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})`,
      color: "white",
      padding: "1rem 2rem",
      border: "none",
      borderRadius: "0.75rem",
      cursor: loading ? "not-allowed" : "pointer",
      fontSize: "1rem",
      fontWeight: "700",
      width: "100%",
      maxWidth: "300px",
      boxShadow: "0 8px 25px rgba(109, 40, 217, 0.3)",
      transition: "all 0.3s ease",
      opacity: loading ? 0.7 : 1,
      position: "relative",
      '@media (min-width: 768px)': {
        width: "220px",
      }
    },
    row: {
      display: "flex",
      flexDirection: "column",
      gap: "2rem",
      '@media (min-width: 1024px)': {
        flexDirection: "row",
      }
    },
    col: {
      flex: "1",
      minWidth: "0",
    },
    loader: {
      display: "inline-block",
      width: "16px",
      height: "16px",
      border: "2px solid transparent",
      borderTop: "2px solid currentColor",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
      marginRight: "0.5rem",
    },
    courseCard: (selected) => ({
      padding: "1.25rem",
      border: `2px solid ${selected ? theme.accent : theme.border}`,
      borderRadius: "0.75rem",
      marginBottom: "0.75rem",
      background: selected ? `${theme.accent}10` : theme.cardBg,
      cursor: "pointer",
      display: "flex",
      alignItems: "flex-start",
      gap: "1rem",
      transition: "all 0.3s ease",
    }),
    buttonGroup: {
      display: "flex",
      flexWrap: "wrap",
      gap: "0.5rem",
      alignItems: "center",
    }
  };

  // ---------------- Effects ----------------
  useEffect(() => {
    const mock = [
      {
        id: 87,
        name: "Cattle Management 101",
        objectives: ["Handling", "Feeding"],
      },
      {
        id: 88,
        name: "Advanced Herd Health",
        objectives: ["Disease", "Vaccination"],
      },
      {
        id: 90,
        name: "Dairy Essentials",
        objectives: ["Milking", "Hygiene"],
      },
    ];
    setAvailableCourses(mock);
  }, []);

  useEffect(() => {
    if (!coverImageFile) return;
    const reader = new FileReader();
    reader.onload = (e) => setCoverImagePreview(e.target.result);
    reader.readAsDataURL(coverImageFile);
  }, [coverImageFile]);

  // ---------------- Handlers ----------------
  const handleButtonClick = async (buttonType, action) => {
    setButtonLoading(prev => ({ ...prev, [buttonType]: true }));
    
    // Simulate API call or state update delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    action();
    
    setButtonLoading(prev => ({ ...prev, [buttonType]: false }));
  };

  const toggleSelectCourse = (id) => {
    setSelectedCourses((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  };

  const toggleCurriculumCourse = (id) => {
    setCurriculumCourses((p) =>
      p.includes(id) ? p.filter((x) => x !== id) : [...p, id]
    );
  };

  // ---------------- Submit ----------------
  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);

    const payload = {
      membership_type: membershipType,
      title,
      description,
      intended_learner: intendedLearner,
      access_type: accessType,
      price: accessType === "paid" ? Number(price) : 0,
      expiry_type: expiryType,
      expiry_months:
        expiryType === "expiry"
          ? Number(expiryMonths || expiryYears * 12)
          : null,
      status,
      course_ids: selectedCourses,
      curriculum_course_ids: curriculumCourses,
      cover_image_base64: coverImagePreview || null,
      promo_video_url: promoVideoUrl || null,
      membership_message: membershipMessage || null,
    };

    try {
      await axios.post(API_CREATE, payload, {
        headers: { "Content-Type": "application/json" },
      });
      alert("Membership Created Successfully!");
      // Reset form
      setTitle("");
      setDescription("");
      setIntendedLearner("");
      setSelectedCourses([]);
      setCurriculumCourses([]);
      setPrice("");
      setExpiryMonths("");
      setExpiryYears("");
      setCoverImageFile(null);
      setCoverImagePreview(null);
      setPromoVideoUrl("");
      setMembershipMessage("");
    } catch (err) {
      console.log(err);
      alert("Error creating membership");
    }

    setLoading(false);
  }

  // ---------------- Loader Component ----------------
  const Loader = ({ size = "16px" }) => (
    <div style={{ ...styles.loader, width: size, height: size }} />
  );

  // ---------------- UI ----------------
  return (
    <div style={styles.page}>
      <motion.div
        style={styles.container}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Global Loader */}
        <AnimatePresence>
          {loading && (
            <motion.div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(255, 255, 255, 0.9)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div style={{ textAlign: "center" }}>
                <Loader size="40px" />
                <p style={{ marginTop: "1rem", color: theme.primary, fontWeight: "600" }}>
                  Creating Membership...
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <h1 style={styles.heading}>Create Membership</h1>
        <p style={styles.sub}>Create and publish memberships for learners</p>

        <form onSubmit={handleSubmit}>
          <div style={styles.row}>
            {/* -------- LEFT COLUMN -------- */}
            <div style={styles.col}>
              {/* Membership Type */}
              <div style={styles.section}>
                <label style={styles.label}>Membership Type</label>
                <div style={styles.buttonGroup}>
                  <motion.button
                    type="button"
                    style={styles.btnChip(membershipType === "basic", buttonLoading.basic)}
                    onClick={() => handleButtonClick("basic", () => setMembershipType("basic"))}
                    whileHover={{ scale: buttonLoading.basic ? 1 : 1.05 }}
                    whileTap={{ scale: buttonLoading.basic ? 1 : 0.95 }}
                    disabled={buttonLoading.basic}
                  >
                    {buttonLoading.basic && <Loader size="12px" />}
                    Basic
                  </motion.button>
                  <motion.button
                    type="button"
                    style={styles.btnChip(membershipType === "pro", buttonLoading.pro)}
                    onClick={() => handleButtonClick("pro", () => setMembershipType("pro"))}
                    whileHover={{ scale: buttonLoading.pro ? 1 : 1.05 }}
                    whileTap={{ scale: buttonLoading.pro ? 1 : 0.95 }}
                    disabled={buttonLoading.pro}
                  >
                    {buttonLoading.pro && <Loader size="12px" />}
                    Pro
                  </motion.button>
                  <motion.button
                    type="button"
                    style={styles.btnChip(membershipType === "enterprise", buttonLoading.enterprise)}
                    onClick={() => handleButtonClick("enterprise", () => setMembershipType("enterprise"))}
                    whileHover={{ scale: buttonLoading.enterprise ? 1 : 1.05 }}
                    whileTap={{ scale: buttonLoading.enterprise ? 1 : 0.95 }}
                    disabled={buttonLoading.enterprise}
                  >
                    {buttonLoading.enterprise && <Loader size="12px" />}
                    Enterprise
                  </motion.button>
                </div>
              </div>

              {/* Title */}
              <div style={styles.section}>
                <label style={styles.label}>Title</label>
                <motion.input
                  style={styles.input}
                  placeholder="Beginner Cattle Management"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  whileFocus={{
                    borderColor: theme.primary,
                    boxShadow: `0 0 0 3px ${theme.primary}20`,
                  }}
                  required
                />
              </div>

              {/* Description */}
              <div style={styles.section}>
                <label style={styles.label}>Description</label>
                <motion.textarea
                  style={styles.textarea}
                  placeholder="Write a short summary about this membership..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  whileFocus={{
                    borderColor: theme.primary,
                    boxShadow: `0 0 0 3px ${theme.primary}20`,
                  }}
                  required
                />
              </div>

              {/* Intended Learner */}
              <div style={styles.section}>
                <label style={styles.label}>Intended Learner</label>
                <motion.input
                  style={styles.input}
                  placeholder="Farmers, livestock owners, agriculture students..."
                  value={intendedLearner}
                  onChange={(e) => setIntendedLearner(e.target.value)}
                  whileFocus={{
                    borderColor: theme.primary,
                    boxShadow: `0 0 0 3px ${theme.primary}20`,
                  }}
                  required
                />
              </div>

              {/* Course Selection */}
              <div style={styles.section}>
                <label style={styles.label}>
                  Select Courses (Membership includes)
                </label>

                {availableCourses.map((c) => (
                  <motion.div
                    key={c.id}
                    style={styles.courseCard(selectedCourses.includes(c.id))}
                    onClick={() => toggleSelectCourse(c.id)}
                    whileHover={{ 
                      scale: 1.02, 
                      boxShadow: "0 4px 15px rgba(0,0,0,0.1)" 
                    }}
                    animate={{
                      borderColor: selectedCourses.includes(c.id) ? theme.accent : theme.border,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCourses.includes(c.id)}
                      readOnly
                      style={{ 
                        width: '1.25rem', 
                        height: '1.25rem',
                        accentColor: theme.accent,
                        marginTop: '0.125rem'
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <b style={{ color: theme.primary, fontSize: "1rem" }}>{c.name}</b>
                      <div style={{ 
                        fontSize: "0.875rem", 
                        color: theme.lightText,
                        marginTop: "0.25rem"
                      }}>
                        {c.objectives.join(" • ")}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Curriculum Selection */}
              <div style={styles.section}>
                <label style={styles.label}>Curriculum Courses</label>
                <div style={styles.card}>
                  {availableCourses.map((c) => (
                    <motion.div
                      key={c.id}
                      style={{ 
                        display: "flex", 
                        alignItems: "center", 
                        marginBottom: "1rem",
                        padding: "0.75rem",
                        borderRadius: "0.5rem",
                        background: curriculumCourses.includes(c.id) ? `${theme.primary}10` : "transparent",
                        border: `1px solid ${curriculumCourses.includes(c.id) ? theme.primary : theme.border}`
                      }}
                      whileHover={{ x: 5 }}
                    >
                      <input
                        type="checkbox"
                        id={`curriculum-${c.id}`}
                        checked={curriculumCourses.includes(c.id)}
                        onChange={() => toggleCurriculumCourse(c.id)}
                        style={{ 
                          marginRight: "1rem", 
                          width: '1.25rem', 
                          height: '1.25rem',
                          accentColor: theme.primary
                        }}
                      />
                      <label 
                        htmlFor={`curriculum-${c.id}`} 
                        style={{ 
                          color: theme.text, 
                          fontWeight: "500",
                          cursor: "pointer",
                          flex: 1
                        }}
                      >
                        {c.name}
                      </label>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            {/* -------- RIGHT COLUMN -------- */}
            <div style={styles.col}>
              {/* Access Type */}
              <div style={styles.section}>
                <label style={styles.label}>Access Type</label>
                <div style={styles.buttonGroup}>
                  <motion.button
                    type="button"
                    style={styles.btnChip(accessType === "free", buttonLoading.free)}
                    onClick={() => handleButtonClick("free", () => setAccessType("free"))}
                    whileHover={{ scale: buttonLoading.free ? 1 : 1.05 }}
                    whileTap={{ scale: buttonLoading.free ? 1 : 0.95 }}
                    disabled={buttonLoading.free}
                  >
                    {buttonLoading.free && <Loader size="12px" />}
                    Free
                  </motion.button>
                  <motion.button
                    type="button"
                    style={styles.btnChip(accessType === "paid", buttonLoading.paid)}
                    onClick={() => handleButtonClick("paid", () => setAccessType("paid"))}
                    whileHover={{ scale: buttonLoading.paid ? 1 : 1.05 }}
                    whileTap={{ scale: buttonLoading.paid ? 1 : 0.95 }}
                    disabled={buttonLoading.paid}
                  >
                    {buttonLoading.paid && <Loader size="12px" />}
                    Paid
                  </motion.button>
                </div>
                <AnimatePresence>
                  {accessType === "paid" && (
                    <motion.input
                      style={styles.input}
                      type="number"
                      placeholder="Price (INR)"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      initial={{ opacity: 0, y: -10, height: 0 }}
                      animate={{ opacity: 1, y: 0, height: "auto" }}
                      exit={{ opacity: 0, y: -10, height: 0 }}
                      transition={{ duration: 0.3 }}
                      whileFocus={{
                        borderColor: theme.primary,
                        boxShadow: `0 0 0 3px ${theme.primary}20`,
                      }}
                      required
                    />
                  )}
                </AnimatePresence>
              </div>

              {/* Expiry */}
              <div style={styles.section}>
                <label style={styles.label}>Expiry Type</label>
                <div style={styles.buttonGroup}>
                  <motion.button
                    type="button"
                    style={styles.btnChip(expiryType === "lifetime", buttonLoading.lifetime)}
                    onClick={() => handleButtonClick("lifetime", () => setExpiryType("lifetime"))}
                    whileHover={{ scale: buttonLoading.lifetime ? 1 : 1.05 }}
                    whileTap={{ scale: buttonLoading.lifetime ? 1 : 0.95 }}
                    disabled={buttonLoading.lifetime}
                  >
                    {buttonLoading.lifetime && <Loader size="12px" />}
                    Lifetime
                  </motion.button>
                  <motion.button
                    type="button"
                    style={styles.btnChip(expiryType === "expiry", buttonLoading.expiry)}
                    onClick={() => handleButtonClick("expiry", () => setExpiryType("expiry"))}
                    whileHover={{ scale: buttonLoading.expiry ? 1 : 1.05 }}
                    whileTap={{ scale: buttonLoading.expiry ? 1 : 0.95 }}
                    disabled={buttonLoading.expiry}
                  >
                    {buttonLoading.expiry && <Loader size="12px" />}
                    Expiry
                  </motion.button>
                </div>

                <AnimatePresence>
                  {expiryType === "expiry" && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, height: 0 }}
                      animate={{ opacity: 1, y: 0, height: "auto" }}
                      exit={{ opacity: 0, y: -10, height: 0 }}
                      transition={{ duration: 0.3 }}
                      style={{ marginTop: '1rem' }}
                    >
                      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: '150px' }}>
                          <label style={{ ...styles.label, fontSize: '0.8rem' }}>Expiry Months</label>
                          <motion.input
                            style={styles.input}
                            type="number"
                            placeholder="Months"
                            value={expiryMonths}
                            onChange={(e) => setExpiryMonths(e.target.value)}
                            whileFocus={{
                              borderColor: theme.primary,
                              boxShadow: `0 0 0 3px ${theme.primary}20`,
                            }}
                          />
                        </div>
                        <div style={{ flex: 1, minWidth: '150px' }}>
                          <label style={{ ...styles.label, fontSize: '0.8rem' }}>Expiry Years</label>
                          <motion.input
                            style={styles.input}
                            type="number"
                            placeholder="Years"
                            value={expiryYears}
                            onChange={(e) => setExpiryYears(e.target.value)}
                            whileFocus={{
                              borderColor: theme.primary,
                              boxShadow: `0 0 0 3px ${theme.primary}20`,
                            }}
                          />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Cover Image */}
              <div style={styles.section}>
                <label style={styles.label}>Cover Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setCoverImageFile(e.target.files[0])}
                  style={{
                    border: `2px solid ${theme.border}`,
                    padding: '1rem',
                    borderRadius: '0.75rem',
                    width: '100%',
                    background: theme.cardBg,
                    fontSize: '0.875rem'
                  }}
                />
                <AnimatePresence>
                  {coverImagePreview && (
                    <motion.img
                      src={coverImagePreview}
                      alt="cover preview"
                      style={{
                        width: "100%",
                        maxWidth: "400px",
                        marginTop: "1rem",
                        borderRadius: "0.75rem",
                        border: `2px solid ${theme.border}`
                      }}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                    />
                  )}
                </AnimatePresence>
              </div>

              {/* Promo Video */}
              <div style={styles.section}>
                <label style={styles.label}>Promo Video URL</label>
                <motion.input
                  style={styles.input}
                  placeholder="https://youtube.com/embed/..."
                  value={promoVideoUrl}
                  onChange={(e) => setPromoVideoUrl(e.target.value)}
                  whileFocus={{
                    borderColor: theme.primary,
                    boxShadow: `0 0 0 3px ${theme.primary}20`,
                  }}
                />
              </div>

              {/* Membership Message */}
              <div style={styles.section}>
                <label style={styles.label}>Membership Message</label>
                <motion.textarea
                  style={styles.textarea}
                  placeholder="Welcome message for members..."
                  value={membershipMessage}
                  onChange={(e) => setMembershipMessage(e.target.value)}
                  whileFocus={{
                    borderColor: theme.primary,
                    boxShadow: `0 0 0 3px ${theme.primary}20`,
                  }}
                />
              </div>

              {/* Status */}
              <div style={styles.section}>
                <label style={styles.label}>Launch Status</label>
                <div style={styles.buttonGroup}>
                  <motion.button
                    type="button"
                    style={styles.btnChip(status === "published", buttonLoading.published)}
                    onClick={() => handleButtonClick("published", () => setStatus("published"))}
                    whileHover={{ scale: buttonLoading.published ? 1 : 1.05 }}
                    whileTap={{ scale: buttonLoading.published ? 1 : 0.95 }}
                    disabled={buttonLoading.published}
                  >
                    {buttonLoading.published && <Loader size="12px" />}
                    Publish
                  </motion.button>
                  <motion.button
                    type="button"
                    style={styles.btnChip(status === "draft", buttonLoading.draft)}
                    onClick={() => handleButtonClick("draft", () => setStatus("draft"))}
                    whileHover={{ scale: buttonLoading.draft ? 1 : 1.05 }}
                    whileTap={{ scale: buttonLoading.draft ? 1 : 0.95 }}
                    disabled={buttonLoading.draft}
                  >
                    {buttonLoading.draft && <Loader size="12px" />}
                    Draft
                  </motion.button>
                </div>
              </div>

              {/* Submit */}
              <div style={{ 
                marginTop: "2rem", 
                display: "flex", 
                justifyContent: "center",
                '@media (min-width: 768px)': {
                  justifyContent: "flex-start",
                }
              }}>
                <motion.button
                  type="submit"
                  style={styles.submit}
                  whileHover={{ 
                    scale: loading ? 1 : 1.05, 
                    boxShadow: loading ? "0 8px 25px rgba(109, 40, 217, 0.3)" : "0 12px 30px rgba(109, 40, 217, 0.4)" 
                  }}
                  whileTap={{ scale: loading ? 1 : 0.95 }}
                  disabled={loading}
                >
                  {loading && <Loader size="16px" />}
                  {loading ? "Creating Membership..." : "Create Membership"}
                </motion.button>
              </div>
            </div>
          </div>
        </form>
      </motion.div>

      {/* Add CSS animation for loader */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}